import classNames from 'classnames';
import React, { useState } from 'react';
import { Button, Col, Collapse, Row } from 'reactstrap';
import { FilterData, FilterState } from '..';
import { LabelledDatePicker } from '../../../../components/LabelledDatePicker';
import { LabelledMultiSelect } from '../../../../components/LabelledMultiSelect';
import { LabelledSelect } from '../../../../components/LabelledSelect';
import styles from './styles.module.scss';
import { SelectOption } from '../../../../components/Select';
import { Trigger } from '../../../../entities/quality_of_hire/Trigger';
import { Location } from '../../../../entities/Location';
import { MoreFilters } from '../../../../components/MoreFilters';
import { humanize } from '../../../../utils/humanize';
import { JobHiringMember } from '../../../../entities/JobHiringMember';

interface PropTypes {
  filterData?: FilterData;
  filterState: FilterState;
  onFilterUpdate: (
    name: keyof FilterState,
    value: FilterState[keyof FilterState],
  ) => void;
  onApplyClick: (appliedHiddenFilters: number) => void;
  onResetClick: () => void;
  appliedHiddenFilters: number;
}

function retrieveHiringMembersNames(hiring_members: JobHiringMember[]) {
  return hiring_members.map((hm) => hm.name);
}

function buildSelectableFilterSelectorOptions(
  filterState: FilterState,
  filterData?: FilterData,
) {
  if (filterData == null) return [];

  const selectedFilter = filterState.selectedFilter;
  let options = [];

  if (selectedFilter.value === 'department') {
    options = filterData.jobs.map((job) => job.department);
  } else if (selectedFilter.value === 'job') {
    options = filterData.jobs.map((job) => job.name);
  } else if (selectedFilter.value === 'hiring_manager') {
    options = filterData.jobs
      .map((job) => retrieveHiringMembersNames(job.hiring_managers))
      .flat();
  } else if (selectedFilter.value === 'recruiter') {
    options = filterData.jobs
      .map((job) => retrieveHiringMembersNames(job.recruiters))
      .flat();
  } else {
    throw new Error(`${selectedFilter.value} not implemented.`);
  }

  return Array.from(new Set(options)).map((value: string) => ({
    label: value,
    value,
  }));
}

function DynamicDataMultiSelect(props: PropTypes) {
  const selectedFilter = props.filterState.selectedFilter;

  return (
    <LabelledMultiSelect
      options={buildSelectableFilterSelectorOptions(
        props.filterState,
        props.filterData,
      )}
      selected={props.filterState.selectedFilterData}
      label={selectedFilter.label}
      placeholder={`All ${humanize(selectedFilter.value)}s`}
      singleSelectedText={`1 selected ${humanize(
        selectedFilter.value,
      ).toLocaleLowerCase()}`}
      manySelectedText={`%d selected ${humanize(
        selectedFilter.value,
      ).toLocaleLowerCase()}s`}
      onChange={(newValues) =>
        props.onFilterUpdate('selectedFilterData', newValues)
      }
      max={10}
      disabled={props.filterData == null}
    />
  );
}

function GeneralLabelledMultiSelect(props: {
  filterData: FilterData;
  options: Location[] | Trigger[];
  selectOptions: SelectOption[];
  label: string;
  placeholder: string;
  option: string;
  onFilterUpdate: (
    name: keyof FilterState,
    value: SelectOption | SelectOption[],
  ) => void;
  selectOptionsName: keyof FilterState;
}) {
  return (
    <LabelledMultiSelect
      options={props.options.map((option) => ({
        label: option.name,
        value: option.id.toString(),
      }))}
      selected={props.selectOptions}
      label={props.label}
      placeholder={props.placeholder}
      singleSelectedText={`1 selected ${props.option}`}
      manySelectedText={`%d selected ${props.option}s`}
      onChange={(newValues) =>
        props.onFilterUpdate(props.selectOptionsName, newValues)
      }
      max={10}
      disabled={props.filterData == null}
    />
  );
}

export function Filters(props: PropTypes) {
  const selectedFilter = props.filterState.selectedFilter;
  const [moreFilters, setMoreFilters] = useState(false);

  const selectedHiddenFiltersCount = +(
    props.filterState.selectedLocations.length > 0
  );

  return (
    <div className='mb-3'>
      <Row className='fs-5 h-100'>
        <Col xs='12' md='4' xl='2' className='mt-2'>
          <LabelledSelect
            options={(() => {
              if (props.filterData == null) return [];

              return props.filterData.selectableFilters;
            })()}
            selected={selectedFilter}
            onChange={(newFilter) =>
              props.onFilterUpdate('selectedFilter', newFilter)
            }
            label='Filter by'
            className={'w-100'}
            disabled={props.filterData == null}
          />
        </Col>
        <Col xs='12' md='4' xl='2' className='mt-2'>
          <DynamicDataMultiSelect {...props} />
        </Col>
        <Col xs='12' md='4' xl='2' className='mt-2'>
          <GeneralLabelledMultiSelect
            filterData={props.filterData}
            options={props.filterData ? props.filterData.triggers : []}
            selectOptions={props.filterState.selectedTriggers}
            label={'Onboarding Stages'}
            placeholder={`All onboarding stages`}
            option={'stage'}
            onFilterUpdate={props.onFilterUpdate}
            selectOptionsName={'selectedTriggers'}
          />
        </Col>
        <Col xs='12' md='4' xl='2' className='mt-2'>
          <LabelledDatePicker
            label='Date'
            dateFilter={props.filterState.dateRange}
            onChange={(start, end, range) =>
              props.onFilterUpdate('dateRange', { start, end, range })
            }
            className={'w-100'}
          />
        </Col>
        <Col
          xs='6'
          md='4'
          xl='2'
          className={classNames(
            'd-flex text-nowrap mt-2 align-bottom align-self-end',
            styles.filterButtonsColumn,
          )}
        >
          <div
            className={classNames(
              'd-flex gap-3 justify-content-between p-0',
              styles.filterButtons,
            )}
          >
            <Button color='secondary' onClick={props.onResetClick}>
              Reset
            </Button>
            <Button
              color='primary'
              onClick={() => props.onApplyClick(selectedHiddenFiltersCount)}
            >
              Apply
            </Button>
          </div>
        </Col>
        <Col
          xs='6'
          md='4'
          xl='2'
          className={classNames(
            'mt-2 align-bottom align-self-end',
            styles.filterButtonsColumn,
          )}
        >
          <MoreFilters
            moreFilters={moreFilters}
            appliedHiddenFilters={props.appliedHiddenFilters}
            onChange={setMoreFilters}
          />
        </Col>
      </Row>
      <Collapse isOpen={moreFilters}>
        <Row className='fs-5 h-100'>
          <Col xs='12' md='4' xl='2' className='mt-2'>
            <GeneralLabelledMultiSelect
              filterData={props.filterData}
              options={props.filterData ? props.filterData.locations : []}
              selectOptions={props.filterState.selectedLocations}
              label={`Locations`}
              placeholder={'All locations'}
              option={'location'}
              onFilterUpdate={props.onFilterUpdate}
              selectOptionsName={'selectedLocations'}
            />
          </Col>
        </Row>
      </Collapse>
    </div>
  );
}
